// uiVersion — 1 NGUỒN SỰ THẬT cho cờ giao diện v1/v2, DÙNG CHUNG SaaS + Core.
//
// v2 = lớp phủ CSS `src/styles/ui-v2.css` của repo này, bật bằng attribute `data-ui="v2"`
// trên <html>. Bỏ attribute = về v1 tức thì, không cần reload, không cần deploy.
//
// ⚠️ LUẬT MẶC ĐỊNH được VIẾT Ở NHIỀU CHỖ, phải sửa TẤT CẢ cùng lúc:
//    1) cnvwork-saas/admin/index.html   — IIFE boot (set attribute TRƯỚC khi React render)
//    2) attendance-app/admin/index.html — IIFE boot của Core
//    3) readUiVersion() dưới đây
//    Luật: CHỈ giá trị 'v1' trong localStorage mới tắt v2. Mọi giá trị khác (kể cả rỗng,
//    rác, chưa từng set) = v2. Nghĩa là v2 là mặc định, user phải chủ động chọn về v1.
//    Viết theo hướng "opt-out" thay vì "opt-in" để đọc localStorage rỗng không phải chờ
//    /auth/me → không có khoảnh khắc nào user thấy v1 loé lên rồi lật sang v2.
//    (Boot script không import được module nên bản lặp là CỐ Ý, không phải sơ suất.)
//
// Nguồn sự thật để RENDER là localStorage (đọc đồng bộ được lúc boot). BE
// (user.preferences.ui_version) chỉ để SEED thiết bị MỚI — đúng luật hadLocalRef ở
// src/pages/me/sections/AppearanceSection.jsx:33-53: nếu máy này đã từng chọn thì KHÔNG
// được để giá trị BE lật ngược lựa chọn của user (sự cố theme 2026-05-30).
//
// Gate tenant (chỉ SaaS có, Core là 1 khách 1 deployment nên không cần) nằm ở
// cnvwork-saas/admin/src/lib/uiVersion.js — nó bọc file này lại.

export const UI_VERSION_KEY = 'ui_version';
export const UI_V1 = 'v1';
export const UI_V2 = 'v2';

/** Sự kiện phát khi cờ đổi trong CÙNG tab (event 'storage' native chỉ bắn sang tab KHÁC). */
export const UI_VERSION_EVENT = 'cnv:ui-version';

export function isValidUiVersion(v) {
  return v === UI_V1 || v === UI_V2;
}

/** Đọc lựa chọn thật của thiết bị này. Trả về null nếu chưa từng chọn. */
export function readStoredUiVersion() {
  try {
    const v = localStorage.getItem(UI_VERSION_KEY);
    return isValidUiVersion(v) ? v : null;
  } catch {
    return null; // Safari private mode / localStorage bị chặn
  }
}

/** Phiên bản đang áp dụng (chưa xét gate tenant — xem bản bọc ở SaaS). */
export function readUiVersion() {
  return readStoredUiVersion() === UI_V1 ? UI_V1 : UI_V2;
}

/** Máy này đã từng chọn chưa — để biết có được phép nhận giá trị seed từ BE hay không. */
export function hasStoredUiVersion() {
  return readStoredUiVersion() !== null;
}

/**
 * Áp cờ lên <html>. KHÔNG ghi localStorage — dùng cho lúc adopt giá trị từ BE
 * hoặc đồng bộ lại sau khi tab khác đổi.
 */
export function applyUiVersion(version) {
  const el = document.documentElement;
  if (version === UI_V2) el.setAttribute('data-ui', UI_V2);
  else el.removeAttribute('data-ui');
}

/** Ghi lựa chọn + áp ngay + báo cho các component khác trong cùng tab. */
export function setUiVersion(version) {
  const v = isValidUiVersion(version) ? version : UI_V2;
  try {
    localStorage.setItem(UI_VERSION_KEY, v);
  } catch { /* không ghi được thì vẫn áp cho phiên hiện tại */ }
  applyUiVersion(v);
  window.dispatchEvent(new CustomEvent(UI_VERSION_EVENT, { detail: v }));
  return v;
}

/**
 * Lắng nghe cờ đổi — cả cùng tab (CustomEvent) và tab khác (storage native).
 * @param {(v: string) => void} cb
 * @param {() => string} [read] hàm đọc phiên bản hiện hành; SaaS truyền bản CÓ gate tenant
 *   vào đây để tab khác đổi cũng không lọt qua gate. Không truyền thì dùng readUiVersion.
 * @returns {() => void} hàm huỷ đăng ký
 */
export function subscribeUiVersion(cb, read = readUiVersion) {
  const onCustom = (e) => cb(isValidUiVersion(e.detail) ? e.detail : read());
  const onStorage = (e) => {
    if (e.key && e.key !== UI_VERSION_KEY) return;
    const v = read();
    applyUiVersion(v); // tab khác đổi → tab này phải đổi theo, không chỉ đổi state
    cb(v);
  };
  window.addEventListener(UI_VERSION_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(UI_VERSION_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
